"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, Waves, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Seasonality & Window Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp demand surges during Rath Yatra and long weekend rush versus quiet monsoon shoulder dips. We build dynamic minimum length-of-stay controls and pace-based pricing so your property never relies on panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pacing & minimum stay restrictions"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com for VIP Road, Marine Drive, and beachside clusters. Automated competitive alerts and yield adjustments protect your RevPAR while securing high-intent bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim margins lost to steep OTA commissions. We enforce strict rate parity across meta-search and drive direct guest conversions through targeted booking incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel boost"
}];
const seasonalityTiers = [{
  season: "Mega Peak: Rath Yatra & Festivities",
  timing: "June - July & Festive Weeks",
  impact: "Extreme surge demand, compressed 3-7 day booking windows",
  strategy: "Strict multi-night minimum stay rules, tiered rate fences, and zero OTA distress allocations.",
  icon: Sparkles
}, {
  season: "High Season: Winter & Coastal Leisure",
  timing: "October - February",
  impact: "Steady pilgrimage inflow & regional leisure weekend travelers from Kolkata & Bhubaneswar",
  strategy: "Premium room category upselling, dynamic weekend vs weekday pacing, and direct booking perks.",
  icon: Sun
}, {
  season: "Value Season: Monsoon & Shoulder",
  timing: "March - May & August - September",
  impact: "High price sensitivity, family leisure shifts, and spontaneous drive-in traffic",
  strategy: "Packaged inclusions, targeted corporate retreats, and tactical channel partner promotions.",
  icon: Waves
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapepuri" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Puri
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Puri is an energetic micro-market shaped by deep-rooted spiritual pilgrimage, seasonal coastal leisure, and high-volume weekend getaways from regional metros. Sunil Singh structures disciplined revenue models calibrated specifically for beachfront resorts, luxury boutique stays, and heritage hotels across Puri.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <div className="mt-16 md:mt-20 rounded-xl border border-border bg-card/60 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 pb-6 border-b border-border/60">
            <div>
              <span className="text-xs font-mono font-semibold tracking-wider uppercase text-secondary">
                Puri Micro-Market Yield Cycles
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-1">
                Seasonal Demand & Yield Matrix
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
              Converting unpredictable pilgrimage and holiday surges into sustained GOPPAR through calibrated pacing rules.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityTiers.map((tier, idx) => {
            const IconComp = tier.icon;
            return <div key={tier.season} data-index={idx} className="rounded-lg border border-border/70 bg-background/80 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-md bg-secondary/15 text-secondary-foreground">
                        <IconComp className="size-5 text-chart-2" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-foreground">
                          {tier.season}
                        </h4>
                        <span className="text-xs font-mono text-muted-foreground">
                          {tier.timing}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                      <strong className="text-foreground">Impact: </strong>
                      {tier.impact}
                    </p>
                  </div>
                  <div className="pt-3 border-t border-border/50 text-xs text-foreground/90">
                    <span className="font-semibold text-secondary block mb-1">Playbook:</span>
                    {tier.strategy}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Puri hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}